import { memo, useState, useEffect } from 'react';
import { Alert, IconButton, Collapse } from '@mui/material';
import ABSSvgIcon from '@abs/core/ABSSvgIcon';
import absWebService from 'app/services/absWebService';

function SystemMessages() {
  const [systemMessages, setSystemMessages] = useState([]);
  const [dismissedMessages, setDismissedMessages] = useState(new Set());

  useEffect(() => {
    // Get initial system messages
    const messages = absWebService.getSystemMessages();
    setSystemMessages(messages || []);

    // Listen for system message updates
    const handleSystemMessagesUpdate = (messages) => {
      setSystemMessages(messages || []);
      // Don't clear dismissed messages - they persist until page reload
    };

    absWebService.on('onSystemMessagesUpdate', handleSystemMessagesUpdate);

    // Cleanup listener on unmount
    return () => {
      absWebService.removeListener('onSystemMessagesUpdate', handleSystemMessagesUpdate);
    };
  }, []);

  const handleDismiss = (messageId) => {
    setDismissedMessages(prev => new Set([...prev, messageId]));
  };

  // Filter out dismissed messages
  const visibleMessages = systemMessages.filter(msg => !dismissedMessages.has(msg.Message_ID));

  // Don't render anything if there are no visible messages
  if (!visibleMessages || visibleMessages.length === 0) {
    return null;
  }

  // Map message types to MUI Alert severity
  const getSeverity = (type) => {
    const typeMap = {
      error: 'error',
      warning: 'warning',
      info: 'info',
      success: 'success',
    };
    return typeMap[type] || 'info';
  };

  return (
    <div className="flex flex-col gap-0 w-full">
      {visibleMessages.map((message) => (
        <Collapse key={message.Message_ID} in={true}>
          <Alert
            severity={getSeverity(message.Type)}
            className="w-full rounded-0"
            sx={{
              '& .MuiAlert-message': {
                width: '100%',
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: 'semibold',
              },
              ...(message.Type === 'error' && {
                backgroundColor: '#f44336',
                color: '#fff',
                '& .MuiAlert-icon': {
                  color: '#fff',
                },
              }),
              ...(message.Type === 'warning' && {
                backgroundColor: '#ff9800',
                color: '#fff',
                '& .MuiAlert-icon': {
                  color: '#fff',
                },
              }),
              ...(message.Type === 'info' && {
                backgroundColor: '#6E6E6E',
                color: '#fff',
                '& .MuiAlert-icon': {
                  color: '#fff',
                },
              }),
              ...(message.Type === 'success' && {
                backgroundColor: '#fff',
                color: '#000',
                border: '1px solid #e0e0e0',
                '& .MuiAlert-icon': {
                  color: '#4caf50',
                },
              }),
            }}
            icon={
              <ABSSvgIcon size={20}>
                {message.Type === 'error' && 'heroicons-outline:exclamation-circle'}
                {message.Type === 'warning' && 'heroicons-outline:exclamation'}
                {message.Type === 'info' && 'heroicons-outline:information-circle'}
                {message.Type === 'success' && 'heroicons-outline:check-circle'}
              </ABSSvgIcon>
            }
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => handleDismiss(message.Message_ID)}
              >
                <ABSSvgIcon size={18}>heroicons-outline:x</ABSSvgIcon>
              </IconButton>
            }
          >
            <span dangerouslySetInnerHTML={{ __html: message.Message }} />
          </Alert>
        </Collapse>
      ))}
    </div>
  );
}

export default memo(SystemMessages);

